import {
  createGyymApiClient,
  GyymApiClient,
  GyymRequest,
  InferGyymRequestParams,
} from '@gyym/sdk'

import type { PrefetchConfig } from './types'

/**
 * Cache TTL.
 * バックエンドの在庫更新が10分周期のため、その半分の5分に設定
 */
const CACHE_TTL_MS = 5 * 60 * 1000

type CacheEntry = {
  value: Promise<unknown>
  expiresAt: number
}

const cache = new Map<string, CacheEntry>()

let sharedClient: GyymApiClient | undefined

const getSharedClient = () => {
  sharedClient ??= createGyymApiClient({
    baseURL: process.env.NEXT_PUBLIC_API_HOST,
  })
  return sharedClient
}

/**
 * Prefetch a user-independent resource through a shared cookie-less client
 * and cache the result in server memory for a short TTL.
 *
 * `gyymPrefetch` と違いリクエストごとのクライアントを使わないため、
 * レスポンスがユーザーに依存しないカタログ系エンドポイント
 * (searchMetadata / prefectures.list など) 専用。
 * 認証情報を送らないので、キャッシュ値は全リクエストで安全に共有できる。
 *
 * @param selectRequest Pick the request from the shared client
 * @param params Request params (included in the cache key)
 */
export const cachedGyymPrefetch = async <T extends GyymRequest>(
  selectRequest: (client: GyymApiClient) => T,
  ...params: InferGyymRequestParams<T>
): Promise<PrefetchConfig<T>> => {
  const request = selectRequest(getSharedClient())
  const key = request.getKey(...params)

  const cached = cache.get(key)
  if (cached && cached.expiresAt > Date.now()) {
    return [key, await cached.value] as PrefetchConfig<T>
  }

  console.log('[Fetch:shared]', key)

  // Store the promise itself so concurrent misses share a single request
  const value = Promise.resolve(request(...params)).catch((e) => {
    // Never cache failures
    cache.delete(key)
    throw e
  })

  cache.set(key, { value, expiresAt: Date.now() + CACHE_TTL_MS })

  return [key, await value] as PrefetchConfig<T>
}
